import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Mail, Database, CheckCircle2, AlertTriangle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function StatusPill({ status }) {
  if (status === "ok" || status === "sent") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wider uppercase font-semibold text-[#00C04B] border border-[#00C04B]/30 bg-[#00C04B]/5 rounded-sm"><CheckCircle2 size={11} /> OK</span>;
  }
  if (status === "partial") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wider uppercase font-semibold text-amber-700 border border-amber-300 bg-amber-50 rounded-sm"><AlertTriangle size={11} /> Partial</span>;
  }
  if (status === "failed" || status === "error") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wider uppercase font-semibold text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 rounded-sm"><AlertCircle size={11} /> Failed</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wider uppercase font-semibold text-gray-600 border border-gray-300 bg-gray-50 rounded-sm">{status || "—"}</span>;
}

function EmailHistorySection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/email/history", { params: { limit: 100 } });
        setItems(data.history || []);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <Card className="rounded-sm border border-gray-200 shadow-none bg-white overflow-hidden" data-testid="email-history-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="email-history-table">
          <thead className="bg-gray-50">
            <tr className="text-[10px] tracking-wider uppercase text-gray-500">
              <th className="text-left px-4 py-3">Sent At</th>
              <th className="text-left px-4 py-3">To / CC</th>
              <th className="text-left px-4 py-3">Subject</th>
              <th className="text-left px-4 py-3">Files</th>
              <th className="text-right px-4 py-3">Rows</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-500">No emails sent yet.</td></tr>}
            {!loading && items.map((it) => (
              <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`email-row-${it.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{new Date(it.sent_at).toLocaleString()}</td>
                <td className="px-4 py-2.5 text-xs text-gray-700 max-w-[260px]">
                  <div className="truncate font-mono" title={(it.to || []).join(", ")}>{(it.to || []).join(", ")}</div>
                  {(it.cc || []).length > 0 && <div className="truncate font-mono text-gray-500" title={`CC: ${(it.cc || []).join(", ")}`}>CC: {(it.cc || []).join(", ")}</div>}
                </td>
                <td className="px-4 py-2.5 max-w-[280px] truncate" title={it.subject}>{it.subject}</td>
                <td className="px-4 py-2.5 text-xs text-gray-700 max-w-[280px]">
                  <div className="truncate" title={(it.files || []).map((f) => f.attachment_name).join(", ")}>
                    {(it.files || []).length} file{(it.files || []).length === 1 ? "" : "s"} · {(it.files || []).map((f) => f.attachment_name).join(", ")}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right font-mono">{(it.total_rows || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CRMHistorySection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/crm/history", { params: { limit: 100 } });
        setItems(data.history || []);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <Card className="rounded-sm border border-gray-200 shadow-none bg-white overflow-hidden" data-testid="crm-history-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="crm-history-table">
          <thead className="bg-gray-50">
            <tr className="text-[10px] tracking-wider uppercase text-gray-500">
              <th className="text-left px-4 py-3">Pushed At</th>
              <th className="text-left px-4 py-3">Pushed Filename</th>
              <th className="text-left px-4 py-3">Source File</th>
              <th className="text-right px-4 py-3">Rows</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Errors</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">No CRM pushes yet.</td></tr>}
            {!loading && items.map((it) => (
              <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`crm-row-${it.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{new Date(it.pushed_at).toLocaleString()}</td>
                <td className="px-4 py-2.5 max-w-[260px] truncate font-mono text-xs" title={it.pushed_filename}>{it.pushed_filename || "—"}</td>
                <td className="px-4 py-2.5 max-w-[260px] truncate text-xs text-gray-600" title={it.original_name}>{it.original_name}</td>
                <td className="px-4 py-2.5 text-right font-mono">{(it.rows || 0).toLocaleString()}</td>
                <td className="px-4 py-2.5"><StatusPill status={it.status} /></td>
                <td className="px-4 py-2.5 text-xs text-[#FF3B30] max-w-[280px]" title={(it.errors || []).join(" · ")}>
                  <div className="truncate">{(it.errors || []).join(" · ")}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Activity() {
  return (
    <div className="p-6 md:p-10 max-w-[1500px]" data-testid="activity-page">
      <div className="mb-8 fade-up">
        <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gray-500">Audit</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-gray-950 mt-2">Activity</h1>
        <p className="text-gray-600 mt-2">Every email send and every CRM push, in one place.</p>
      </div>

      <Tabs defaultValue="email" className="w-full" data-testid="activity-tabs">
        <TabsList className="rounded-sm bg-gray-100">
          <TabsTrigger value="email" data-testid="tab-email-history" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:text-[#002FA7]"><Mail size={14} className="mr-1.5" /> Email History</TabsTrigger>
          <TabsTrigger value="crm" data-testid="tab-crm-history" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:text-[#002FA7]"><Database size={14} className="mr-1.5" /> CRM Pushes</TabsTrigger>
        </TabsList>
        <TabsContent value="email" className="mt-6"><EmailHistorySection /></TabsContent>
        <TabsContent value="crm" className="mt-6"><CRMHistorySection /></TabsContent>
      </Tabs>
    </div>
  );
}
